import { useCallback } from "react"
import {
  useAdForgeGetBrandKitQuery,
  useAdForgeProxyImageMutation,
  useAdForgeProxyVideoMutation,
} from "~/ui-rtk/api/adForgeApi"
import { useGetAssetQuery } from "~/ui-rtk/api/assetsApi"
import type { ScrapedImage } from "../../types"
import useAssetUpload from "../useAssetUpload"
import {
  exportImageWithOverlay,
  exportVideoWithOverlay,
  loadImage,
} from "./export-overlay"

/**
 * Just-in-time overlay baking. Assigning a layout to a card only stores the
 * doc (live preview); this hook does the actual compositing + upload when the
 * ad is CREATED (save draft) — so cards stay non-destructive while editing.
 */

const MAX_CANVAS_EDGE = 2048

/**
 * Design-space dims for a placement ratio label (e.g. "1.91:1", "9:16") —
 * 1080 wide, height derived.
 */
export function dimsForRatioLabel(ratio: string): {
  width: number
  height: number
} {
  const [w, h] = ratio.split(":").map(Number)
  if (!w || !h) return { width: 1080, height: 1080 }
  return { width: 1080, height: Math.round((1080 * h) / w) }
}

export function measureMediaDims(
  asset: ScrapedImage,
): Promise<{ width: number; height: number }> {
  const cap = (w: number, h: number): { width: number; height: number } => {
    const width = w || 1080
    const height = h || 1080
    const scale = Math.min(1, MAX_CANVAS_EDGE / Math.max(width, height))
    return {
      width: Math.round(width * scale),
      height: Math.round(height * scale),
    }
  }
  if (asset.width && asset.height) {
    return Promise.resolve(cap(asset.width, asset.height))
  }
  if (asset.type === "video") {
    return new Promise(resolve => {
      const video = document.createElement("video")
      video.onloadedmetadata = () =>
        resolve(cap(video.videoWidth, video.videoHeight))
      video.onerror = () => resolve(cap(1080, 1080))
      video.src = asset.url
    })
  }
  return loadImage(asset.url).then(
    img => cap(img.naturalWidth, img.naturalHeight),
    () => cap(1080, 1080),
  )
}

export interface UseOverlayBakerResult {
  /**
   * Bake `asset.overlayDoc` onto the media, upload the result and return the
   * saved ScrapedImage (without overlay fields — it's baked in). Returns the
   * asset unchanged when it carries no overlay.
   */
  bakeOverlay: (
    asset: ScrapedImage,
    onProgress?: (fraction: number) => void,
  ) => Promise<ScrapedImage>
  /**
   * Composite `asset.overlayDoc` onto the media and return the FILE without
   * uploading — for the card's download action. Throws when the asset
   * carries no overlay.
   */
  bakeOverlayToFile: (
    asset: ScrapedImage,
    onProgress?: (fraction: number) => void,
  ) => Promise<File>
}

export function useOverlayBaker(): UseOverlayBakerResult {
  const { data: brandKit } = useAdForgeGetBrandKitQuery()
  const { data: logoAsset } = useGetAssetQuery(brandKit?.logoAssetId ?? "", {
    skip: !brandKit?.logoAssetId,
  })
  const logoUrl = logoAsset?.url || logoAsset?.thumbnailUrl || null
  const [proxyImage] = useAdForgeProxyImageMutation()
  const [proxyVideo] = useAdForgeProxyVideoMutation()
  const { uploadFiles } = useAssetUpload()

  const bakeOverlayToFile = useCallback(
    async (
      asset: ScrapedImage,
      onProgress?: (fraction: number) => void,
    ): Promise<File> => {
      const doc = asset.overlayDoc
      if (!doc) throw new Error("This asset has no layout applied")
      const isVideo = asset.type === "video"

      const baseSrc = isVideo
        ? (await proxyVideo({ videoUri: asset.url }).unwrap()).videoUrl
        : (await proxyImage({ url: asset.url }).unwrap()).base64
      const needsLogo = doc.sections.some(section =>
        section.items.some(item => item.type === "logo"),
      )
      const logo =
        needsLogo && logoUrl
          ? await proxyImage({ url: logoUrl })
              .unwrap()
              .then(({ base64 }) => loadImage(base64))
          : null

      return isVideo
        ? exportVideoWithOverlay({
            videoSrc: baseSrc,
            doc,
            assets: { logo },
            onProgress,
          })
        : exportImageWithOverlay({ baseSrc, doc, assets: { logo } })
    },
    [proxyImage, proxyVideo, logoUrl],
  )

  const bakeOverlay = useCallback(
    async (
      asset: ScrapedImage,
      onProgress?: (fraction: number) => void,
    ): Promise<ScrapedImage> => {
      const doc = asset.overlayDoc
      if (!doc) return asset
      const file = await bakeOverlayToFile(asset, onProgress)

      const [baked] = await uploadFiles([file], {
        sourceType: "GEN_AI",
        parentAssetId: asset.assetId,
        metadata: {
          generatedBy: "ai_brand_overlay",
          aspectRatio: `${doc.canvas.width}:${doc.canvas.height}`,
          overlayDoc: doc,
          ...(asset.overlayLayoutId
            ? { overlayLayoutId: asset.overlayLayoutId }
            : {}),
        },
      })
      if (!baked) throw new Error("Failed to save the overlaid media")
      return baked
    },
    [bakeOverlayToFile, uploadFiles],
  )

  return { bakeOverlay, bakeOverlayToFile }
}
